import React, { useState, useMemo, useEffect } from 'react';
import { Plus, Search, Gauge, Fuel, DollarSign, CreditCard, Trash2, Images } from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import { useCollection } from '../../../hooks/useCollection';
import CarForm from './CarForm';
import CarCreditModal from './CarCreditModal';
import EngineControlModal from './EngineControlModal';
import CarDetailsModal from './CarDetailsModal';
import { plateMatches } from '../utils/plateUtils';

export default function CarsPage({ settings, onOpenInfo }) {
  const { lang, t } = useLanguage();
  const isAr = lang === 'ar';

  const { data: cars, loading, add, update, remove } = useCollection('cars');

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [creditModalCar, setCreditModalCar] = useState(null);
  const [engineControlCar, setEngineControlCar] = useState(null);
  const [detailsCar, setDetailsCar] = useState(null);

  // Keep creditModalCar in sync with fresh data updates
  useEffect(() => {
    if (creditModalCar) {
      const fresh = cars.find(c => c.id === creditModalCar.id);
      if (fresh && JSON.stringify(fresh) !== JSON.stringify(creditModalCar)) {
        setCreditModalCar(fresh);
      }
    }
  }, [cars, creditModalCar]);

  const filtered = useMemo(() => {
    return cars.filter(c =>
      (c.brand || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.model || '').toLowerCase().includes(search.toLowerCase()) ||
      // Plate match ignores separators/city code so "123456" finds "123456 WW".
      plateMatches(c.plate, search) ||
      (c.city || '').toLowerCase().includes(search.toLowerCase())
    );
  }, [cars, search]);

  const handleDelete = async (id) => {
    if (window.confirm(t('confirm_delete_car') || (isAr ? 'هل تريد حذف هذه السيارة؟' : 'Supprimer cette voiture?'))) {
      try {
        await remove(id);
      } catch (err) {
        alert('❌ Erreur: ' + err.message);
      }
    }
  };

  const handleSave = async (id, payload) => {
    try {
      if (id) {
        await update(id, payload);
        return { id };
      }
      // add() returns the inserted row ({ id }) — needed so the form can attach
      // gallery images to the freshly created car.
      return await add(payload);
    } catch (err) {
      alert('❌ Erreur: ' + err.message);
      throw err;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <h1 className="text-2xl font-bold text-gray-900">
          {`${t('cars_title') || (isAr ? 'السيارات' : 'Véhicules')} (${cars.length})`}
        </h1>
        <button 
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium shadow transition-all duration-200"
        >
          <Plus className="w-5 h-5" /> 
          {t('add_car') || (isAr ? 'إضافة سيارة' : 'Ajouter voiture')}
        </button>
      </div>

      <div className="relative mb-4">
        <Search className={`absolute ${isAr ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400`} />
        <input 
          type="text" 
          value={search} 
          onChange={e => setSearch(e.target.value)}
          placeholder={t('search_cars_placeholder') || (isAr ? 'البحث عن سيارة...' : 'Rechercher une voiture...')}
          className={`w-full ${isAr ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition`}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(car => {
          const effectiveKm = (parseFloat(car.km) || 0) + (parseFloat(car.gpsOdometer) || 0);

          return (
            <div key={car.id} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      {car.brand && (
                        <img
                          src={`https://logo.clearbit.com/${car.brand.toLowerCase().replace(/[^a-z0-9]/g, '').replace('mercedesbenz', 'mercedes-benz')}.com`}
                          alt={car.brand}
                          className="w-7 h-7 object-contain rounded bg-gray-50 flex-shrink-0"
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <h3 className="font-bold text-lg text-gray-900">{`${car.brand} ${car.model}`}</h3>
                    </div>
                    <p className="text-sm text-gray-500 font-mono mt-0.5">{`${car.year || ''} • ${car.plate}`}</p>
                    {car.chassisNumber && (
                      <p className="text-[11px] text-gray-500 font-mono mt-1 flex items-center gap-1">
                        <span className="text-[9px] font-extrabold text-gray-500 bg-gray-100 border border-gray-200 px-1 py-0.5 rounded uppercase tracking-wide">{isAr ? 'الهيكل' : 'Châssis'}</span>
                        <span className="font-bold text-gray-700 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-200">{car.chassisNumber}</span>
                      </p>
                    )}
                    {car.gpsAddress && (
                      <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                        📍 {car.gpsAddress}
                      </p>
                    )}
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold uppercase ${
                    car.status === 'available' ? 'bg-green-100 text-green-700' :
                    car.status === 'rented' ? 'bg-blue-100 text-blue-700' : 'bg-gray-150 bg-gray-100 text-gray-700'
                  }`}>
                    {car.status === 'rented' ? (t('status_rented') || (isAr ? 'مؤجرة' : 'Louée')) :
                     car.status === 'maintenance' ? (t('status_maintenance') || (isAr ? 'صيانة' : 'Entretien')) : 
                     (t('status_available') || (isAr ? 'متاحة' : 'Disponible'))}
                  </span>
                </div>

                <div className="space-y-2 text-sm text-gray-650 text-gray-600 mb-4 border-t border-gray-50 pt-3">
                  <div className="flex items-center gap-2">
                    <Gauge className="w-4 h-4 text-gray-400" /> 
                    <span>{`${effectiveKm.toFixed(0)} km`}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Fuel className="w-4 h-4 text-gray-400" /> 
                    <span>{car.fuel ? (t('fuel_' + car.fuel.toLowerCase()) || car.fuel) : (t('fuel_essence') || 'Essence')}</span>
                  </div>
                  <div className="flex items-center gap-2 font-semibold text-gray-900">
                    <DollarSign className="w-4 h-4 text-gray-400" /> 
                    <span>{`${car.dailyRate || 0} ${settings.currency} / ${isAr ? 'يوم' : 'jour'}`}</span>
                  </div>

                  {/* GPS tracking km summaries */}
                  {(car.gpsOdometer || car.gpsKmDaily) ? (
                    <div className="pt-2 border-t border-gray-100 bg-green-50/50 -mx-3 px-3 py-2 my-1 rounded-md text-xs">
                      <div className="flex items-center gap-1.5 font-bold text-green-800 mb-1">
                        📡 {isAr ? 'متابعة GPS' : 'Suivi GPS'}
                      </div>
                      {car.gpsOdometer ? (
                        <div className="flex items-center justify-between font-mono">
                          <span className="text-gray-500">{isAr ? 'مجموع كم (GPS):' : 'Total km (GPS):'}</span>
                          <span className="font-bold text-green-700">
                            {`${car.gpsOdometer.toFixed(1)} km`}
                            {car.gpsOdometerSource === 'sensor' && <span className="ms-1 text-[9px] bg-green-200 px-1 rounded">🛰️</span>}
                            {car.gpsOdometerSource === 'calculated' && <span className="ms-1 text-[9px] bg-blue-200 px-1 rounded">📐</span>}
                          </span>
                        </div>
                      ) : null}
                      {car.gpsKmDaily ? (
                        <div className="flex items-center justify-between font-mono mt-0.5">
                          <span className="text-gray-500">{isAr ? 'اليوم:' : "Aujourd'hui:"}</span>
                          <span className="font-bold text-blue-700">{`${car.gpsKmDaily.toFixed(1)} km`}</span>
                        </div>
                      ) : null}
                      {car.gpsKmPrevDay ? (
                        <div className="flex items-center justify-between font-mono text-[10px] text-gray-400 mt-0.5">
                          <span>{isAr ? 'أمس:' : 'Hier:'}</span>
                          <span>{`${car.gpsKmPrevDay.toFixed(1)} km`}</span>
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="pt-2 border-t border-gray-100 text-xs flex justify-between text-gray-500">
                    <span>{t('total_rentals') || (isAr ? 'عدد مرات الكراء:' : 'Locations au total:')}</span>
                    <span className="font-bold text-gray-900">{`${car.totalRented || 0}x`}</span>
                  </div>

                  {car.credit && (
                    <div className="pt-2 border-t border-gray-100 text-xs">
                      <div className="flex justify-between text-gray-500 mb-1">
                        <span>{t('remaining_credit') || (isAr ? 'القرض المتبقي:' : 'Crédit restant:')}</span>
                        <span className="font-bold text-red-650 font-mono">{`${car.creditRemaining || 0} ${settings.currency}`}</span>
                      </div>
                      <div className="w-full bg-gray-250 bg-gray-250 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full transition-all"
                          style={{ width: `${100 - (((car.creditRemaining || 0) / (car.creditTotal || 1)) * 100)}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Document alerts */}
                  {(car.taxExpiry || car.insuranceExpiry || car.techVisitExpiry) && (
                    <div className="pt-2 border-t border-gray-100 text-xs">
                      <p className="font-bold text-gray-700 mb-1.5">📋 Documents Expirations:</p>
                      <div className="space-y-1">
                        {[
                          { field: 'taxExpiry', icon: '🏛️', label: 'Dariba' },
                          { field: 'insuranceExpiry', icon: '🛡️', label: 'Assurance' },
                          { field: 'techVisitExpiry', icon: '🔧', label: 'V. Tech.' }
                        ].filter(d => car[d.field]).map(d => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const exp = new Date(car[d.field]);
                          exp.setHours(0, 0, 0, 0);
                          const days = Math.ceil((exp - today) / (24 * 60 * 60 * 1000));
                          
                          let bgClass, textClass, status;
                          if (days < 0) {
                            bgClass = 'bg-red-50 border-red-200';
                            textClass = 'text-red-700';
                            status = isAr ? `⛔ منتهي (${Math.abs(days)}ي)` : `⛔ Expiré (${Math.abs(days)}j)`;
                          } else if (days <= 5) {
                            bgClass = 'bg-red-50 border-red-150';
                            textClass = 'text-red-650';
                            status = isAr ? `🚨 متبقي ${days}ي` : `🚨 ${days}j restants`;
                          } else if (days <= 15) {
                            bgClass = 'bg-amber-50 border-amber-150';
                            textClass = 'text-amber-700';
                            status = isAr ? `⚠️ متبقي ${days}ي` : `⚠️ ${days}j restants`;
                          } else {
                            bgClass = 'bg-green-50 border-green-150';
                            textClass = 'text-green-700';
                            status = isAr ? `✓ ${days}ي` : `✓ ${days}j`;
                          }
                          
                          return (
                            <div key={d.field} className={`flex items-center justify-between text-[11px] px-2 py-0.5 rounded border ${bgClass}`}>
                              <span className="font-medium text-gray-700">{`${d.icon} ${d.label}`}</span>
                              <span className={`font-bold ${textClass}`}>{status}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Card Actions */}
              <div className="flex gap-1.5 mt-2 pt-3 border-t border-gray-100 flex-wrap">
                <button 
                  onClick={() => setCreditModalCar(car)}
                  className={`flex-1 border py-2 rounded-lg text-xs font-bold transition flex items-center justify-center gap-1.5 min-w-[80px] ${
                    car.credit 
                      ? 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100' 
                      : 'bg-white text-gray-650 border-gray-200 hover:bg-gray-50 hover:text-gray-800'
                  }`}
                  title={isAr ? 'الإئتمان البنكي' : 'Crédit bancaire'}
                >
                  <CreditCard className="w-3.5 h-3.5" />
                  <span>{car.credit ? (isAr ? '💳 إئتمان' : '💳 Crédit') : (isAr ? 'إئتمان' : 'Crédit')}</span>
                  {car.credit && (() => {
                    const unpaid = (car.bankInstallments || []).filter(i => !i.paid);
                    const today = new Date().toISOString().split('T')[0];
                    const overdue = unpaid.filter(i => i.dueDate < today);
                    if (overdue.length > 0) {
                      return <span className="px-1.5 py-0.2 bg-red-500 text-white text-[9px] font-bold rounded-full animate-pulse">{overdue.length} ⚠️</span>;
                    }
                    if (unpaid.length > 0) {
                      return <span className="px-1.5 py-0.2 bg-amber-250 text-amber-900 text-[9px] font-bold rounded-full">{unpaid.length}</span>;
                    }
                    return null;
                  })()}
                </button>

                {car.traccarUniqueId && (
                  <button
                    onClick={() => setEngineControlCar(car)}
                    className="flex items-center justify-center gap-1 bg-amber-50 text-amber-700 border border-amber-300 hover:bg-amber-100 px-3 py-2 rounded-lg text-xs font-bold transition"
                    title={isAr ? 'التحكم في مضخة البنزين' : 'Contrôle Pompe Essence'}
                  >
                    <span>⚡</span>
                  </button>
                )}

                <button
                  onClick={() => onOpenInfo?.(car)}
                  className="flex items-center justify-center gap-1 text-gray-600 border border-gray-200 hover:bg-gray-50 px-2.5 py-2 rounded-lg text-xs font-bold transition"
                  title={isAr ? 'معلومات السيارة' : 'Informations véhicule'}
                >
                  <Images className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => { setEditing(car); setShowForm(true); }}
                  className="flex-1 text-blue-600 border border-blue-200 hover:bg-blue-50 py-2 rounded-lg text-xs font-bold transition min-w-[50px]"
                >
                  {isAr ? 'تعديل' : 'Modifier'}
                </button>
                <button 
                  onClick={() => handleDelete(car.id)}
                  className="text-red-650 border border-red-200 hover:bg-red-50 px-2.5 py-2 rounded-lg transition"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })}
        
        {filtered.length === 0 && (
          <p className="col-span-full text-center py-12 text-gray-500 font-medium bg-white rounded-xl border border-gray-150 border-dashed">
            {t('no_cars_found') || (isAr ? 'لم يتم العثور على أي سيارات' : 'Aucune voiture trouvée')}
          </p>
        )}
      </div>

      {showForm && (
        <CarForm 
          car={editing} 
          cars={cars}
          settings={settings} 
          onSave={handleSave} 
          onClose={() => { setShowForm(false); setEditing(null); }} 
        />
      )}

      {detailsCar && (
        <CarDetailsModal
          car={detailsCar}
          settings={settings}
          onClose={() => setDetailsCar(null)}
        />
      )}

      {creditModalCar && (
        <CarCreditModal
          car={creditModalCar} 
          settings={settings} 
          onUpdate={handleSave} 
          onClose={() => setCreditModalCar(null)} 
        />
      )}

      {engineControlCar && (
        <EngineControlModal 
          car={engineControlCar} 
          settings={settings} 
          onClose={() => setEngineControlCar(null)} 
        />
      )}
    </div>
  );
}
